/**
 * Create student — UX/UI Specification §9.5.
 *
 * Six fields and nothing else. This form is the studio's first impression of
 * whether the tool is fast, and anything that can be added later is added
 * later (PRD §6).
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { calculateBmi, formatValue, LIMITS } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { useApiData } from '../hooks/useApiData.js';
import { useSession } from '../session/SessionContext.js';
import { NumberField, numberFieldError } from '../components/ui/NumberField.js';
import { messageFor } from '../api/error-messages.js';
import { GENDER_LABELS, type Gender } from '../api/types.js';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import './StudentCreate.css';

export function StudentCreate() {
  const { session } = useSession();
  const owner = session?.role === 'owner';
  const instructors = useApiData<{ items: { id: string; name: string }[] }>(
    '/api/instructors',
  );

  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState<Gender>('female');
  const [instructorId, setInstructorId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');

  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const height = Number(heightCm.replace(',', '.'));
  const weight = Number(weightKg.replace(',', '.'));
  const bmi = calculateBmi(weight, height);

  // A value the server would reject should not travel: the field already names
  // the range it wanted, where a rejected POST only says the request was bad.
  const outOfRange =
    numberFieldError(heightCm, LIMITS.heightCm) !== null ||
    numberFieldError(weightKg, LIMITS.weightKg) !== null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setFieldErrors({});

    try {
      const created = await api.post<{ id: string }>('/api/students', {
        firstName,
        lastName,
        gender,
        // Ignored by the API for an instructor: their student is their own.
        instructorId: instructorId || null,
        dateOfBirth,
        heightCm: height,
        weightKg: weight,
      });
      navigate(`/ogrenciler/${created.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(messageFor(err.code));
        const fields = err.details?.fields;
        if (Array.isArray(fields)) {
          const next: Record<string, string> = {};
          for (const field of fields) {
            if (typeof field === 'string') next[field] = 'Bu alanı kontrol edin.';
          }
          setFieldErrors(next);
        }
      } else {
        setError(messageFor('INTERNAL_ERROR'));
      }
      // The entered values stay: a validation error must never cost the user
      // the form they just filled (PRD §18).
      setBusy(false);
    }
  }

  return (
    <section className="create">
      <h1>Yeni Öğrenci</h1>
      <p className="create__lede">
        Sadece bu alanlar gerekli. Ölçüm, fotoğraf ve hedefleri sonradan ekleyebilirsiniz.
      </p>

      <form onSubmit={submit} className="create__form" noValidate>
        <div className="create__row">
          <Field
            label="Ad"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={fieldErrors.firstName}
            autoComplete="off"
            required
          />
          <Field
            label="Soyad"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={fieldErrors.lastName}
            autoComplete="off"
            required
          />
        </div>

        <fieldset className="create__genders">
          <legend className="create__legend">Cinsiyet</legend>
          <div className="create__chips">
            {(Object.keys(GENDER_LABELS) as Gender[]).map((value) => (
              <label key={value} className={gender === value ? 'chip chip--on' : 'chip'}>
                <input
                  type="radio"
                  name="gender"
                  value={value}
                  checked={gender === value}
                  onChange={() => setGender(value)}
                  className="pp-visually-hidden"
                />
                {GENDER_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>

        {/*
         * Who will see this student. Only the owner chooses: an instructor
         * adding a student is adding their own, and letting them name someone
         * else would be handing a student on, which is the owner's to do.
         */}
        {owner && (
          <label className="create__field">
            <span>Eğitmen</span>
            <select
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
            >
              <option value="">Yalnızca yönetici görsün</option>
              {(instructors.data?.items ?? []).map((instructor) => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </option>
              ))}
            </select>
            <em>Bu öğrenciyi yalnızca seçtiğiniz eğitmen ve siz görürsünüz.</em>
          </label>
        )}

        <Field
          label="Doğum Tarihi"
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          error={fieldErrors.dateOfBirth}
          required
        />

        <div className="create__row">
          <NumberField
            label="Boy (cm)"
            limit={LIMITS.heightCm}
            value={heightCm}
            onChange={setHeightCm}
            required
          />
          <NumberField
            label="Kilo (kg)"
            limit={LIMITS.weightKg}
            value={weightKg}
            onChange={setWeightKg}
            hint={bmi === null ? undefined : `BMI ${formatValue(bmi, '', 1)}`}
            required
          />
        </div>

        {error && (
          <p className="create__error" role="alert">
            {error}
          </p>
        )}

        <div className="create__actions">
          <Link to="/ogrenciler" className="btn btn--secondary">
            Vazgeç
          </Link>
          <Button type="submit" loading={busy} disabled={outOfRange}>
            {busy ? 'Kaydediliyor…' : 'Öğrenciyi Kaydet'}
          </Button>
        </div>
      </form>
    </section>
  );
}
